import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Linkedin, Mail } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  linkedin: string;
  email: string;
  experience?: string;
}

interface TeamData {
  members: TeamMember[];
}

// Optimized animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export function Team() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<TeamData>('/data/team.json');

  // SSR-safe animation props
  const headerAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const teamMembers = data?.members || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-eco-green-50 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-semibold rounded-full mb-4"
          >
            Our Team
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Meet the People Behind{' '}
            <span className="bg-gradient-to-r from-eco-green-600 to-eco-teal-500 bg-clip-text text-transparent">
              Eco Enviro
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A passionate team committed to building India's circular economy
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 rounded-full bg-gray-200" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        )}

        {/* Team Grid */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6"
          >
            {teamMembers.map((member) => (
              <motion.div
                key={member.name}
                variants={itemVariants}
                className="group"
              >
                <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all duration-300 text-center">
                  {/* Circular Image */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 rounded-full overflow-hidden bg-white ring-4 ring-eco-green-100 group-hover:ring-eco-green-300 transition-all">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  {/* Name & Role */}
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 group-hover:text-eco-green-700 transition-colors leading-tight">
                    {member.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-eco-green-600 mt-1">
                    {member.role}
                  </p>
                  {member.experience && (
                    <p className="text-xs text-gray-500 mt-1 mb-3 line-clamp-2">
                      {member.experience}
                    </p>
                  )}
                  {!member.experience && <div className="mb-3" />}
                  {/* Contact Buttons */}
                  <div className="flex justify-center gap-2">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-eco-green-100 hover:bg-eco-green-600 rounded-full flex items-center justify-center text-eco-green-600 hover:text-white transition-all duration-300"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                    <a
                      href={`mailto:${member.email}`}
                      className="w-8 h-8 bg-eco-green-100 hover:bg-eco-green-600 rounded-full flex items-center justify-center text-eco-green-600 hover:text-white transition-all duration-300"
                      title="Email"
                    >
                      <Mail className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
